/**
 * useLogin - Hook for handling user login.
 *
 * Provides a mutation hook for logging in users via the backend API.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authQueryKeys, storeToken, storeUser, clearAuth } from '../utils/authStorage';
import type { LoginCredentials, LoginResponse, AuthUser } from '../types';

const API_BASE_URL = '/api';

/**
 * API error response format.
 */
interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    trace_id: string;
  };
}

/**
 * Data envelope for single resources.
 */
interface DataEnvelope<T> {
  data: T;
}

/**
 * Login and fetch user info from the API.
 *
 * @param credentials - Login credentials (email and password)
 * @returns Promise that resolves to AuthUser on success
 */
async function login(credentials: LoginCredentials): Promise<AuthUser> {
  // Step 1: Call /auth/login to get access token
  const loginResponse = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!loginResponse.ok) {
    const errorData = (await loginResponse.json().catch(() => ({
      error: { message: 'Login failed' },
    }))) as ApiErrorResponse;
    throw new Error(errorData.error?.message || 'Invalid email or password');
  }

  const loginData = (await loginResponse.json()) as DataEnvelope<LoginResponse>;
  const token = loginData.data.access_token;

  // Step 2: Store token immediately so we can use it
  storeToken(token);

  // Step 3: Call /auth/me to get user info
  const meResponse = await fetch(`${API_BASE_URL}/auth/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!meResponse.ok) {
    // Clear the token if we can't get user info
    clearAuth();
    throw new Error('Failed to fetch user info');
  }

  const meData = (await meResponse.json()) as DataEnvelope<AuthUser>;
  const user = meData.data;

  // Step 4: Store user info
  storeUser(user);

  return user;
}

/**
 * Hook for handling login mutation.
 *
 * @returns Mutation object with:
 * - `mutate`: Function to trigger login
 * - `mutateAsync`: Async function to trigger login
 * - `isPending`: Boolean indicating if login is in progress
 * - `error`: Error object if login failed
 * - `isSuccess`: Boolean indicating if login succeeded
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: login,
    onSuccess: (user: AuthUser) => {
      // Update React Query cache with user data
      queryClient.setQueryData(authQueryKeys.currentUser(), user);
    },
    onError: () => {
      // Clear any existing auth data on error
      clearAuth();
      queryClient.setQueryData(authQueryKeys.currentUser(), null);
    },
  });
}
